import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, FileDown, Tablet, Plus, Ban, RotateCcw, Copy, ChevronLeft, ChevronRight, CalendarRange, AlertTriangle } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TIMECLOCK } from "@/constants/testIds";
import { toast } from "sonner";

const STATUS_LABELS = {
  sin_marcar: { label: "Sin marcar", color: "bg-charcoal/5 text-charcoal/50" },
  en_turno: { label: "En turno", color: "bg-emerald-50 text-emerald-700" },
  completo: { label: "Completo", color: "bg-petroleum/10 text-petroleum" },
};

const mondayOf = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day; // domingo (0) -> retrocede 6 días
  date.setDate(date.getDate() + diff);
  return date.toISOString().slice(0, 10);
};

const shiftWeek = (weekStartStr, deltaDays) => {
  const date = new Date(`${weekStartStr}T00:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return date.toISOString().slice(0, 10);
};

const copyToClipboard = async (text, successMessage) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.info(successMessage);
  } catch {
    toast.error("No se pudo copiar automáticamente. Copia el enlace manualmente: " + text);
  }
};

export default function Jornada() {
  const [roster, setRoster] = useState([]);
  const [devices, setDevices] = useState([]);
  const [captureOpen, setCaptureOpen] = useState(null);
  const [captureForm, setCaptureForm] = useState({ event_type: "entrada", note: "" });
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [deviceForm, setDeviceForm] = useState({ label: "", work_center: "" });
  const [exportRange, setExportRange] = useState({ start: "", end: "" });
  const [exportFormat, setExportFormat] = useState("pdf");
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [weeklyReportFormat, setWeeklyReportFormat] = useState("pdf");
  const [faltaNotifications, setFaltaNotifications] = useState(null);
  const [resolvingNotificationId, setResolvingNotificationId] = useState(null);
  const [workCenters, setWorkCenters] = useState([]);

  const loadRoster = () => api.get("/timeclock/today").then(({ data }) => setRoster(data.roster));
  const loadDevices = () => api.get("/timeclock/kiosk-devices").then(({ data }) => setDevices(data));
  const loadWeeklyReport = (start) => api.get(`/timeclock/weekly-report?week_start=${start}`).then(({ data }) => setWeeklyReport(data));
  const loadFaltaNotifications = () => api.get("/timeclock/falta-notifications").then(({ data }) => setFaltaNotifications(data));

  useEffect(() => { loadRoster(); loadDevices(); loadFaltaNotifications(); api.get("/work-centers").then(({ data }) => setWorkCenters(data)); }, []);
  useEffect(() => { loadWeeklyReport(weekStart); }, [weekStart]);

  const resolveFaltaNotification = async (id, action) => {
    setResolvingNotificationId(id);
    try {
      const { data } = await api.post(`/timeclock/falta-notifications/${id}/${action}`, {});
      toast.success(data.message);
      await loadFaltaNotifications();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setResolvingNotificationId(null);
    }
  };

  const submitCapture = async () => {
    try {
      await api.post(`/employees/${captureOpen}/timeclock/admin-capture`, captureForm);
      toast.success("Marcaje capturado por administrador");
      setCaptureOpen(null);
      setCaptureForm({ event_type: "entrada", note: "" });
      loadRoster();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo capturar el marcaje");
    }
  };

  const createDevice = async () => {
    try {
      const { data } = await api.post("/timeclock/kiosk-devices", { label: deviceForm.label, work_center: deviceForm.work_center || null });
      toast.success("Kiosko creado. Abre el enlace en la tablet del centro de trabajo.");
      setDeviceOpen(false);
      setDeviceForm({ label: "", work_center: "" });
      loadDevices();
      copyToClipboard(`${window.location.origin}/kiosko/${data.token}`, "Enlace copiado al portapapeles");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo crear el kiosko");
    }
  };

  const toggleDevice = async (device) => {
    await api.post(`/timeclock/kiosk-devices/${device.id}/${device.active ? "deactivate" : "reactivate"}`);
    loadDevices();
  };

  const download = async (url, filename) => {
    const res = await api.get(url, { responseType: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(res.data);
    link.download = filename;
    link.click();
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight flex items-center gap-3">
          <Clock className="w-7 h-7 text-petroleum" strokeWidth={1.5} /> Registro de Jornada
        </h1>
        <p className="text-charcoal/60 mt-1">Entrada y salida de hoy — Art. 132 fracc. XXXIV de la LFT.</p>

        <div className="flex items-center gap-2 mt-6">
          <Input data-testid={TIMECLOCK.exportStartInput} type="date" value={exportRange.start} onChange={(e) => setExportRange({ ...exportRange, start: e.target.value })} className="bg-white w-40" />
          <Input data-testid={TIMECLOCK.exportEndInput} type="date" value={exportRange.end} onChange={(e) => setExportRange({ ...exportRange, end: e.target.value })} className="bg-white w-40" />
          <select
            data-testid={TIMECLOCK.exportFormatSelect}
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm"
          >
            <option value="pdf">PDF</option>
            <option value="csv">CSV</option>
          </select>
          <Button
            data-testid={TIMECLOCK.exportButton}
            variant="outline"
            onClick={() => download(`/timeclock/export?start=${exportRange.start}&end=${exportRange.end}&format=${exportFormat}`, `jornada_empresa.${exportFormat}`)}
          >
            <FileDown className="w-4 h-4 mr-2" /> Exportar
          </Button>
        </div>

        <div className="mt-6 bg-card border border-petroleum/10 rounded-xl divide-y divide-petroleum/10">
          {roster.map((r) => {
            const s = STATUS_LABELS[r.status] || STATUS_LABELS.sin_marcar;
            return (
              <div key={r.employee_id} data-testid={TIMECLOCK.rosterRow} className="flex items-center justify-between p-4">
                <div>
                  <p className="text-sm font-semibold text-charcoal">{r.full_name}</p>
                  <p className="text-xs text-charcoal/50">{r.work_center || "Sin centro de trabajo asignado"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span data-testid={TIMECLOCK.rosterStatusBadge} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.color}`}>
                    {r.entrada_time ? `Entrada ${r.entrada_time}${r.entrada_method === "admin_capture" ? " (admin)" : ""}` : s.label}
                    {r.salida_time ? ` · Salida ${r.salida_time}${r.salida_method === "admin_capture" ? " (admin)" : ""}` : ""}
                  </span>
                  {(r.entrada_location_status === "fuera_rango" || r.salida_location_status === "fuera_rango") && (
                    <span data-testid={TIMECLOCK.rosterLocationOutOfRangeBadge} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Fuera de rango</span>
                  )}
                  {(r.entrada_location_status === "sin_ubicacion" || r.salida_location_status === "sin_ubicacion") && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-charcoal/5 text-charcoal/40">Sin ubicación</span>
                  )}
                  <Dialog open={captureOpen === r.employee_id} onOpenChange={(o) => setCaptureOpen(o ? r.employee_id : null)}>
                    <DialogTrigger asChild>
                      <Button data-testid={TIMECLOCK.manualCaptureButton} size="sm" variant="outline">Captura manual</Button>
                    </DialogTrigger>
                    <DialogContent data-testid={TIMECLOCK.manualCaptureDialog} className="bg-bone">
                      <DialogHeader><DialogTitle>Captura manual — {r.full_name}</DialogTitle></DialogHeader>
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                        Este registro quedará marcado como "Capturado por administrador", no como autoservicio. Úsalo solo si el trabajador no tiene celular ni acceso al kiosko.
                      </p>
                      <div className="space-y-3">
                        <div>
                          <Label>Tipo</Label>
                          <select
                            data-testid={TIMECLOCK.manualCaptureEventType}
                            value={captureForm.event_type}
                            onChange={(e) => setCaptureForm({ ...captureForm, event_type: e.target.value })}
                            className="w-full border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm mt-1"
                          >
                            <option value="entrada">Entrada</option>
                            <option value="salida">Salida</option>
                          </select>
                        </div>
                        <div>
                          <Label>Motivo (obligatorio)</Label>
                          <Input data-testid={TIMECLOCK.manualCaptureNote} value={captureForm.note} onChange={(e) => setCaptureForm({ ...captureForm, note: e.target.value })} placeholder="Ej. No tiene celular ni acceso al kiosko" className="bg-white mt-1" />
                        </div>
                        <Button data-testid={TIMECLOCK.manualCaptureSubmit} onClick={submitCapture} disabled={!captureForm.note} className="w-full bg-petroleum rounded-full">Registrar</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Link to={`/admin/empleados/${r.employee_id}?tab=jornada`} className="text-xs text-petroleum font-medium hover:underline">Ver historial</Link>
                </div>
              </div>
            );
          })}
          {roster.length === 0 && <p className="p-6 text-sm text-charcoal/50">No hay empleados activos.</p>}
        </div>

        {faltaNotifications?.notifications?.some((n) => n.status === "pending") && (
          <div className="mt-10" data-testid={TIMECLOCK.faltaNotificationsSection}>
            <h2 className="font-heading text-xl font-semibold text-charcoal flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-terracotta" strokeWidth={1.5} /> Notificaciones de Falta Pendientes
            </h2>
            <p className="text-xs text-charcoal/50 mb-3">
              Estos empleados alcanzaron {faltaNotifications.notifications[0]?.threshold ?? ""} retardo(s) o más dentro del periodo de nómina actual. Revisa y confirma para agregar la falta a su expediente, o descarta si no aplica — nunca se crea automáticamente.
            </p>
            <div className="bg-card border border-terracotta/30 rounded-xl divide-y divide-petroleum/10">
              {faltaNotifications.notifications.filter((n) => n.status === "pending").map((n) => (
                <div key={n.id} data-testid={TIMECLOCK.faltaNotificationRow} className="flex items-center justify-between p-4 flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-semibold text-charcoal">{n.employee_name}</p>
                    <p className="text-xs text-charcoal/50">{n.retardos_count} retardo(s) del {n.period_start} al {n.period_end} (umbral: {n.threshold})</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid={TIMECLOCK.faltaNotificationDismissButton}
                      size="sm" variant="outline" disabled={resolvingNotificationId === n.id}
                      onClick={() => resolveFaltaNotification(n.id, "dismiss")}
                    >
                      Descartar
                    </Button>
                    <Button
                      data-testid={TIMECLOCK.faltaNotificationConfirmButton}
                      size="sm" disabled={resolvingNotificationId === n.id}
                      onClick={() => resolveFaltaNotification(n.id, "confirm")}
                      className="bg-terracotta hover:bg-terracotta/90 rounded-full"
                    >
                      Confirmar falta
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10" data-testid={TIMECLOCK.weeklyReportSection}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="font-heading text-xl font-semibold text-charcoal flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-petroleum" strokeWidth={1.5} /> Reporte Semanal de Asistencia
            </h2>
            <div className="flex items-center gap-2">
              <Button data-testid={TIMECLOCK.weeklyReportPrevWeek} size="sm" variant="outline" onClick={() => setWeekStart((w) => shiftWeek(w, -7))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span data-testid={TIMECLOCK.weeklyReportRange} className="text-sm text-charcoal/70 font-medium min-w-[170px] text-center">
                {weeklyReport ? `${weeklyReport.week_start} al ${weeklyReport.week_end}` : "Cargando..."}
              </span>
              <Button data-testid={TIMECLOCK.weeklyReportNextWeek} size="sm" variant="outline" onClick={() => setWeekStart((w) => shiftWeek(w, 7))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-charcoal/50 mb-3">
            {weeklyReport?.expected_checkin_time
              ? `Retardo = entrada después de las ${weeklyReport.expected_checkin_time}${weeklyReport.tolerance_minutes ? ` + ${weeklyReport.tolerance_minutes} min de tolerancia` : ""}. Falta = día laborable sin ningún marcaje.`
              : "Configura el horario de entrada esperado en Perfil de Empresa para detectar retardos. Las faltas (día laborable sin marcaje) ya se calculan sin ese dato."}
          </p>
          <div className="bg-card border border-petroleum/10 rounded-xl divide-y divide-petroleum/10">
            <div className="flex items-center p-3 text-xs font-semibold text-charcoal/50 uppercase tracking-wide">
              <span className="flex-1">Empleado</span>
              <span className="w-28 text-center">Trabajados</span>
              <span className="w-20 text-center">Faltas</span>
              <span className="w-20 text-center">Retardos</span>
            </div>
            {(weeklyReport?.rows || []).map((r) => (
              <div key={r.employee_id} data-testid={TIMECLOCK.weeklyReportRow} className="flex items-center p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-charcoal">{r.full_name}</p>
                  <p className="text-xs text-charcoal/50">{r.work_center || "Sin centro de trabajo"}</p>
                </div>
                <span className="w-28 text-center text-sm text-charcoal/80">{r.dias_trabajados}</span>
                <span className={`w-20 text-center text-sm font-semibold ${r.faltas > 0 ? "text-red-600" : "text-charcoal/40"}`}>{r.faltas}</span>
                <span className={`w-20 text-center text-sm font-semibold ${r.retardos > 0 ? "text-amber-600" : "text-charcoal/40"}`}>{r.retardos}</span>
              </div>
            ))}
            {weeklyReport && weeklyReport.rows.length === 0 && <p className="p-6 text-sm text-charcoal/50">No hay empleados activos.</p>}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <select
              data-testid={TIMECLOCK.weeklyReportFormatSelect}
              value={weeklyReportFormat}
              onChange={(e) => setWeeklyReportFormat(e.target.value)}
              className="border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm"
            >
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
            </select>
            <Button
              data-testid={TIMECLOCK.weeklyReportExportButton}
              variant="outline"
              onClick={() => download(`/timeclock/weekly-report/export?week_start=${weekStart}&format=${weeklyReportFormat}`, `reporte_semanal_${weekStart}.${weeklyReportFormat}`)}
            >
              <FileDown className="w-4 h-4 mr-2" /> Descargar reporte de esta semana
            </Button>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-xl font-semibold text-charcoal flex items-center gap-2">
              <Tablet className="w-5 h-5 text-petroleum" strokeWidth={1.5} /> Kioskos
            </h2>
            <Dialog open={deviceOpen} onOpenChange={setDeviceOpen}>
              <DialogTrigger asChild>
                <Button data-testid={TIMECLOCK.kioskCreateButton} size="sm" className="bg-petroleum rounded-full"><Plus className="w-4 h-4 mr-1" /> Activar kiosko</Button>
              </DialogTrigger>
              <DialogContent data-testid={TIMECLOCK.kioskCreateDialog} className="bg-bone">
                <DialogHeader><DialogTitle>Activar nuevo kiosko</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Nombre / ubicación</Label>
                    <Input data-testid={TIMECLOCK.kioskCreateLabel} value={deviceForm.label} onChange={(e) => setDeviceForm({ ...deviceForm, label: e.target.value })} placeholder="Ej. Tablet entrada Sucursal Centro" className="bg-white mt-1" />
                  </div>
                  <div>
                    <Label>Centro de trabajo (opcional — déjalo vacío si cualquier empleado puede marcar aquí)</Label>
                    <select data-testid={TIMECLOCK.kioskCreateWorkCenter} value={deviceForm.work_center} onChange={(e) => setDeviceForm({ ...deviceForm, work_center: e.target.value })} className="w-full bg-white mt-1 border border-petroleum/20 rounded-lg px-3 py-2 text-sm">
                      <option value="">Sin restricción de centro de trabajo</option>
                      {workCenters.map((wc) => <option key={wc.id} value={wc.name}>{wc.name}</option>)}
                    </select>
                  </div>
                  <Button data-testid={TIMECLOCK.kioskCreateSubmit} onClick={createDevice} disabled={!deviceForm.label} className="w-full bg-petroleum rounded-full">Crear enlace de activación</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div data-testid={TIMECLOCK.kioskDeviceList} className="bg-card border border-petroleum/10 rounded-xl divide-y divide-petroleum/10">
            {devices.map((d) => (
              <div key={d.id} data-testid={TIMECLOCK.kioskDeviceRow} className="flex items-center justify-between p-4">
                <div>
                  <p className="text-sm font-semibold text-charcoal">{d.label}</p>
                  <p className="text-xs text-charcoal/50">{d.work_center ? `Centro de trabajo: ${d.work_center}` : "Sin restricción de centro de trabajo"} · {d.active ? "Activo" : "Desactivado"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={TIMECLOCK.kioskDeviceLink}
                    onClick={() => copyToClipboard(`${window.location.origin}/kiosko/${d.token}`, "Enlace copiado")}
                    className="text-xs text-petroleum font-medium hover:underline flex items-center gap-1"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar enlace
                  </button>
                  <Button data-testid={TIMECLOCK.kioskDeviceDeactivate} size="sm" variant="outline" onClick={() => toggleDevice(d)}>
                    {d.active ? <><Ban className="w-3.5 h-3.5 mr-1" /> Desactivar</> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reactivar</>}
                  </Button>
                </div>
              </div>
            ))}
            {devices.length === 0 && <p className="p-6 text-sm text-charcoal/50">Sin kioskos activados todavía.</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
